import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Stack, Chip, Button,
    TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
    IconButton, Tooltip, Pagination, alpha, useTheme, LinearProgress,
    Drawer, Divider, Checkbox, Avatar,
} from '@mui/material';
import {
    Group as GroupIcon,
    TrendingUp as TrendingUpIcon,
    Send as SendIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Email as EmailIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    BarChart as ChartIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Replay as ResendIcon,
    Schedule as ScheduleIcon,
    Campaign as CampaignIcon,
} from '@mui/icons-material';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { newsletterApi } from '../services/api';

// ── Colour palette ─────────────────────────────────────────────────────────────
const SEGMENT_COLORS = ['#6366f1','#a855f7','#10d98a','#f59e0b','#ef4444','#06b6d4'];

const STATUS_HEX = {
    active:               '#10d98a',
    pending_verification: '#f59e0b',
    unsubscribed:         '#94a3b8',
    bounced:              '#ef4444',
    complained:           '#ef4444',
};

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color = '#6366f1', sub }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Card sx={{
            borderRadius: '20px', height: '100%', position: 'relative', overflow: 'hidden',
            border: `1px solid ${alpha(color, isDark ? 0.2 : 0.15)}`,
            bgcolor: alpha(color, isDark ? 0.06 : 0.04),
            boxShadow: `0 4px 24px ${alpha(color, 0.1)}`,
            '&:hover': { transform: 'none' },
        }}>
            {/* Glow blob */}
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(color, isDark ? 0.25 : 0.15)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <CardContent sx={{ p: 3, position: 'relative' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: alpha(color, isDark ? 0.8 : 0.7), mb: 1 }}>{label}</Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: '2.2rem', lineHeight: 1, letterSpacing: '-0.03em', color }}>{value ?? '—'}</Typography>
                        {sub && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{sub}</Typography>}
                    </Box>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: alpha(color, isDark ? 0.2 : 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

// ── Subscriber detail drawer ───────────────────────────────────────────────────
function SubscriberDrawer({ subscriber, open, onClose, onDeleted, onResent }) {
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';
    const [deleting, setDeleting] = useState(false);
    const [resending, setResending] = useState(false);
    const [msg, setMsg] = useState(null);

    if (!subscriber) return null;
    const onb = subscriber.onboarding || {};

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await newsletterApi.deleteSubscriber(subscriber.id);
            onDeleted(subscriber.id);
            onClose();
        } catch {
            setMsg({ type: 'error', text: 'Delete failed. Please try again.' });
        } finally {
            setDeleting(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            await newsletterApi.resendWelcome(subscriber.id);
            setMsg({ type: 'success', text: 'Welcome email queued.' });
        } catch {
            setMsg({ type: 'error', text: 'Resend failed.' });
        } finally {
            setResending(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: {
                width: { xs: '100%', sm: 420 }, p: 0,
                bgcolor: isDark ? '#0f172a' : 'background.paper',
                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            } }}>

            {/* Drawer header */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, background: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>Subscriber Detail</Typography>
                    <IconButton onClick={onClose} size="small" sx={{ borderRadius: '8px' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                </Stack>
                {/* Avatar + email + chips */}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#6366f1', 0.15), color: '#6366f1', fontWeight: 800 }}>
                        {subscriber.email?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{subscriber.email}</Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap">
                            <Chip
                                label={subscriber.status?.replace(/_/g, ' ') || 'unknown'}
                                size="small"
                                sx={{
                                    borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem',
                                    bgcolor: alpha(STATUS_HEX[subscriber.status] || '#94a3b8', 0.12),
                                    color: STATUS_HEX[subscriber.status] || '#94a3b8',
                                    border: `1px solid ${alpha(STATUS_HEX[subscriber.status] || '#94a3b8', 0.25)}`,
                                    textTransform: 'capitalize',
                                }}
                            />
                            {subscriber.segment && (
                                <Chip
                                    label={subscriber.segment}
                                    size="small"
                                    sx={{
                                        borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem',
                                        bgcolor: alpha('#6366f1', 0.12), color: '#6366f1',
                                        border: `1px solid ${alpha('#6366f1', 0.25)}`,
                                    }}
                                />
                            )}
                            {subscriber.priority > 0 && (
                                <Chip
                                    label={`Priority: ${subscriber.priority}`}
                                    size="small"
                                    sx={{
                                        borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem',
                                        bgcolor: subscriber.priority > 10 ? alpha('#10d98a', 0.12) : alpha(theme.palette.divider, 0.1),
                                        color: subscriber.priority > 10 ? '#10d98a' : 'text.secondary',
                                        border: subscriber.priority > 10 ? `1px solid ${alpha('#10d98a', 0.25)}` : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            {/* Rest of drawer content */}
            <Box sx={{ p: 3 }}>
                {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2, borderRadius: '10px' }}>{msg.text}</Alert>}

                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.1em' }}>Onboarding Answers</Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5, mb: 3 }}>
                    {[
                        ['Role',        onb.role        || '—'],
                        ['Platforms',   (onb.platforms || []).join(', ') || '—'],
                        ['Pain Point',  onb.painPoint   || '—'],
                    ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{k}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{v}</Typography>
                        </Stack>
                    ))}
                </Stack>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.1em' }}>Referral & Metadata</Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5, mb: 3 }}>
                    {[
                        ['Referral Code',   subscriber.refCode         || '—'],
                        ['Referrals Made',  subscriber.referralCount   ?? 0],
                        ['Referred By',     subscriber.referredBy      || '—'],
                        ['Signed Up',       subscriber.createdAt ? new Date(subscriber.createdAt).toLocaleString() : '—'],
                    ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{k}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{String(v)}</Typography>
                        </Stack>
                    ))}
                </Stack>

                <Divider sx={{ mb: 3 }} />

                <Stack spacing={1.5}>
                    <Button
                        fullWidth variant="outlined"
                        startIcon={resending ? <CircularProgress size={16} color="inherit" /> : <ResendIcon />}
                        onClick={handleResend} disabled={resending}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                    >
                        {resending ? 'Sending…' : 'Resend Welcome Email'}
                    </Button>
                    <Button
                        fullWidth variant="outlined" color="error"
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                        onClick={handleDelete} disabled={deleting}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                    >
                        {deleting ? 'Deleting…' : 'Delete Subscriber'}
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
}

// ── Broadcast composer dialog ─────────────────────────────────────────────────
function BroadcastDialog({ open, onClose, segments, onSent }) {
    const theme     = useTheme();
    const isDark    = theme.palette.mode === 'dark';
    const [subject,     setSubject]     = useState('');
    const [bodyHtml,    setBodyHtml]    = useState('');
    const [segment,     setSegment]     = useState('');
    const [scheduledAt, setScheduledAt] = useState('');
    const [loading,     setLoading]     = useState(false);
    const [result,      setResult]      = useState(null);
    const [error,       setError]       = useState(null);
    const [previewing,  setPreviewing]  = useState(false);

    const reset = () => { setSubject(''); setBodyHtml(''); setSegment(''); setScheduledAt(''); setResult(null); setError(null); setPreviewing(false); };
    const handleClose = () => { reset(); onClose(); };

    const handleSend = async () => {
        if (!subject.trim() || !bodyHtml.trim()) { setError('Subject and body are required.'); return; }
        setLoading(true); setError(null);
        try {
            const scheduledIso = scheduledAt ? new Date(scheduledAt).toISOString() : null;
            const res = await newsletterApi.broadcast({
                subject, bodyHtml, segment: segment || null, scheduledAt: scheduledIso
            });
            setResult(res);
            if (onSent) onSent();
        } catch (err) {
            setError(err.message || 'Broadcast failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{ sx: {
                borderRadius: '20px',
                bgcolor: isDark ? '#0f172a' : 'background.paper',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                backdropFilter: 'blur(20px)',
            } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <SendIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Send Broadcast</Typography>
                </Stack>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {result ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                            {result.scheduled ? 'Broadcast Scheduled!' : 'Broadcast Sent!'}
                        </Typography>
                        {result.scheduled ? (
                            <Typography color="text.secondary">Your broadcast is queued and will be dispatched at the scheduled time.</Typography>
                        ) : (
                            <Typography color="text.secondary">✅ {result.sent} delivered &nbsp;|&nbsp; ❌ {result.failed} failed</Typography>
                        )}
                    </Box>
                ) : (
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                fullWidth label="Subject line" value={subject}
                                onChange={e => setSubject(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                            <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel>Audience</InputLabel>
                                <Select value={segment} label="Audience" onChange={e => setSegment(e.target.value)} sx={{ borderRadius: '12px' }}>
                                    <MenuItem value="">All active subscribers</MenuItem>
                                    {segments.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* Schedule (optional) */}
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <ScheduleIcon sx={{ color: 'text.secondary' }} />
                            <TextField
                                label="Schedule send (optional)"
                                type="datetime-local"
                                value={scheduledAt}
                                onChange={e => setScheduledAt(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Leave empty to send immediately"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, flexGrow: 1 }}
                            />
                        </Stack>

                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Email body (HTML)</Typography>
                                <Button size="small" onClick={() => setPreviewing(p => !p)} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                    {previewing ? 'Edit' : 'Preview'}
                                </Button>
                            </Stack>
                            {previewing ? (
                                <Box sx={{ p: 2, minHeight: 260, borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', overflow: 'auto' }}
                                    dangerouslySetInnerHTML={{ __html: bodyHtml || '<p style="color:#666">Nothing to preview yet.</p>' }}
                                />
                            ) : (
                                <TextField
                                    fullWidth multiline rows={10}
                                    placeholder={'<p>Hello,</p>\n<p>Your message here...</p>\n<p><a href="{{unsubscribe_url}}">Unsubscribe</a></p>'}
                                    value={bodyHtml}
                                    onChange={e => setBodyHtml(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontFamily: 'monospace', fontSize: '0.85rem' } }}
                                />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Use <code>{'{{unsubscribe_url}}'}</code> for the unsubscribe link (auto-injected per recipient if omitted).
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {result ? (
                    <Button onClick={handleClose} variant="contained" sx={{ borderRadius: '12px' }}>Done</Button>
                ) : (
                    <>
                        <Button onClick={handleClose} sx={{ borderRadius: '12px', textTransform: 'none' }}>Cancel</Button>
                        <Button
                            onClick={handleSend}
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : scheduledAt ? <ScheduleIcon /> : <SendIcon />}
                            sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
                        >
                            {loading ? 'Processing…' : scheduledAt ? 'Schedule' : `Send${segment ? ` to ${segment}` : ' to All'}`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

// ── Campaign history tab ───────────────────────────────────────────────────────
function CampaignsTab({ refreshKey = 0 }) {
    const theme = useTheme();
    const [campaigns, setCampaigns] = useState([]);
    const [total,     setTotal]     = useState(0);
    const [page,      setPage]      = useState(1);
    const [loading,   setLoading]   = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await newsletterApi.getCampaigns({ page, perPage: 20 });
            setCampaigns(data.campaigns || []);
            setTotal(data.total || 0);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [page]);

    useEffect(() => { load(); }, [load, refreshKey]);

    if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Campaign History</Typography>
                <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
            </Stack>

            {campaigns.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                    <CampaignIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                    <Typography>No campaigns sent yet.</Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Subject', 'Audience', 'Status', 'Sent', 'Failed', 'Date'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', borderColor: alpha(theme.palette.divider, 0.1) }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {campaigns.map(c => (
                                <TableRow key={c.id} hover sx={{ '& td': { borderColor: alpha(theme.palette.divider, 0.06) } }}>
                                    <TableCell sx={{ fontWeight: 600, maxWidth: 240 }}>
                                        <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.subject}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{c.segment || 'All'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={c.status}
                                            size="small"
                                            color={c.status === 'sent' ? 'success' : c.status === 'scheduled' ? 'warning' : 'error'}
                                            sx={{ fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>{c.sent ?? '—'}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: c.failed > 0 ? 'error.main' : 'text.secondary' }}>{c.failed ?? '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                        {c.sentAt ? new Date(c.sentAt).toLocaleString() : c.scheduled_at ? `Scheduled: ${new Date(c.scheduled_at).toLocaleString()}` : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {total > 20 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination count={Math.ceil(total / 20)} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" />
                </Box>
            )}
        </Box>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const AdminNewsletter = () => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [tab,          setTab]          = useState(0);   // 0=Overview, 1=Subscribers, 2=Campaigns

    // Stats
    const [stats,        setStats]        = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Subscribers list + filters
    const [subscribers,  setSubscribers]  = useState([]);
    const [total,        setTotal]        = useState(0);
    const [page,         setPage]         = useState(1);
    const [perPage]                       = useState(50);
    const [subLoading,   setSubLoading]   = useState(true);
    const [search,       setSearch]       = useState('');
    const [filterSeg,    setFilterSeg]    = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Bulk selection
    const [selected,           setSelected]           = useState(new Set());
    const [bulkLoading,        setBulkLoading]        = useState(false);
    const [bulkMsg,            setBulkMsg]            = useState(null);
    const [confirmBulkDelete,  setConfirmBulkDelete]  = useState(false);

    // Subscriber detail drawer
    const [drawerSub,    setDrawerSub]    = useState(null);

    // Broadcast
    const [broadcastOpen,       setBroadcastOpen]       = useState(false);
    const [campaignRefreshKey,  setCampaignRefreshKey]  = useState(0);

    const segments = stats ? Object.keys(stats.segmentBreakdown || {}) : [];

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try { const data = await newsletterApi.getStats(); setStats(data); }
        catch { /* silent */ }
        finally { setStatsLoading(false); }
    }, []);

    const loadSubscribers = useCallback(async () => {
        setSubLoading(true);
        try {
            const data = await newsletterApi.getSubscribers({ segment: filterSeg || undefined, status: filterStatus, page, perPage });
            setSubscribers(data.subscribers || []);
            setTotal(data.total || 0);
        } catch { /* silent */ }
        finally { setSubLoading(false); }
    }, [filterSeg, filterStatus, page, perPage]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadSubscribers(); }, [loadSubscribers]);

    const filteredSubs = search
        ? subscribers.filter(s => s.email?.toLowerCase().includes(search.toLowerCase()))
        : subscribers;

    const pieData = stats
        ? Object.entries(stats.segmentBreakdown || {}).map(([name, value]) => ({ name, value }))
        : [];

    // ── CSV export ──────────────────────────────────────────────────────────────
    const handleExport = async () => {
        try {
            const blob = await newsletterApi.exportCsv();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `newsletter_${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setBulkMsg({ type: 'error', text: 'Export failed. Please try again.' });
        }
    };

    // ── Bulk actions ────────────────────────────────────────────────────────────
    const toggleSelect = (id) => setSelected(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleSelectAll = () => {
        if (selected.size === filteredSubs.length) setSelected(new Set());
        else setSelected(new Set(filteredSubs.map(s => s.id)));
    };

    const handleBulkDelete = async () => {
        setBulkLoading(true); setBulkMsg(null);
        const ids = [...selected];
        let ok = 0, fail = 0;
        for (const id of ids) {
            try { await newsletterApi.deleteSubscriber(id); ok++; } catch { fail++; }
        }
        setSelected(new Set());
        await loadSubscribers();
        await loadStats();
        setBulkLoading(false);
        setBulkMsg({ type: fail > 0 ? 'warning' : 'success', text: `Deleted ${ok}${fail > 0 ? `, ${fail} failed` : ''}.` });
    };

    const handleBulkResend = async () => {
        setBulkLoading(true); setBulkMsg(null);
        const ids = [...selected];
        let ok = 0, fail = 0;
        for (const id of ids) {
            try { await newsletterApi.resendWelcome(id); ok++; } catch { fail++; }
        }
        setSelected(new Set());
        setBulkLoading(false);
        setBulkMsg({ type: fail > 0 ? 'warning' : 'success', text: `Resent welcome to ${ok}${fail > 0 ? `, ${fail} failed` : ''}.` });
    };

    const handleSubscriberDeleted = (id) => {
        setSubscribers(prev => prev.filter(s => s.id !== id));
        setTotal(t => Math.max(0, t - 1));
        loadStats();
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#10d98a', mb: 0.5 }}>Admin</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1 }}>Newsletter</Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.95rem' }}>Manage waitlist subscribers, view growth stats, and send broadcasts.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        <Tooltip title="Refresh">
                            <IconButton onClick={() => { loadStats(); loadSubscribers(); }} sx={{ borderRadius: '12px', border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                                <RefreshIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleExport}
                            sx={{ borderRadius: '100px', fontWeight: 700, textTransform: 'none', px: 2.5, borderColor: alpha(theme.palette.divider, 0.3), color: 'text.secondary', '&:hover': { borderColor: '#6366f1', color: '#6366f1' } }}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={() => setBroadcastOpen(true)}
                            sx={{ borderRadius: '100px', fontWeight: 800, textTransform: 'none', px: 3, bgcolor: '#6366f1', boxShadow: '0 4px 20px rgba(99,102,241,0.4)', '&:hover': { bgcolor: '#818cf8' } }}
                        >
                            Send Broadcast
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {bulkMsg && (
                <Alert severity={bulkMsg.type} onClose={() => setBulkMsg(null)} sx={{ mb: 3, borderRadius: '12px' }}>{bulkMsg.text}</Alert>
            )}

            {/* Pill tab switcher */}
            <Box sx={{ display: 'flex', mb: 4, overflowX: 'auto' }}>
                <Box sx={{
                    display: 'inline-flex', p: 0.75, borderRadius: '16px',
                    bgcolor: alpha(theme.palette.divider, isDark ? 0.1 : 0.08),
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    gap: 0.5,
                }}>
                    {[
                        { label: 'Overview',    idx: 0 },
                        { label: 'Subscribers', idx: 1, badge: stats?.total ?? total },
                        { label: 'Campaigns',   idx: 2 },
                    ].map(({ label, idx, badge }) => (
                        <Button
                            key={idx}
                            onClick={() => setTab(idx)}
                            sx={{
                                borderRadius: '10px', fontWeight: 700, textTransform: 'none',
                                px: 2.5, py: 0.9, minWidth: 110,
                                bgcolor: tab === idx ? (isDark ? 'rgba(255,255,255,0.08)' : 'background.paper') : 'transparent',
                                color: tab === idx ? (idx === 0 ? '#10d98a' : idx === 1 ? '#6366f1' : '#f59e0b') : 'text.secondary',
                                boxShadow: tab === idx ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                                '&:hover': { bgcolor: tab === idx ? undefined : alpha(theme.palette.divider, 0.08) },
                                transition: 'all 0.2s',
                            }}
                        >
                            {label}
                            {badge > 0 && (
                                <Box component="span" sx={{ ml: 1, px: 0.8, py: 0.1, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 900, bgcolor: tab === 1 ? alpha('#6366f1', 0.15) : alpha(theme.palette.divider, 0.15), color: tab === 1 ? '#6366f1' : 'text.secondary' }}>
                                    {badge}
                                </Box>
                            )}
                        </Button>
                    ))}
                </Box>
            </Box>

            {/* ── Tab 0: Overview ─────────────────────────────────────────────── */}
            {tab === 0 && (
                <Box>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard label="Total Subscribers" value={stats?.total?.toLocaleString()} icon={<GroupIcon />} color="#6366f1" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard label="New This Week" value={stats?.weeklyNew?.toLocaleString()} icon={<TrendingUpIcon />} color="#10d98a" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard label="Segments" value={segments.length} icon={<ChartIcon />} color="#a855f7" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard label="Displaying" value={filteredSubs.length} icon={<EmailIcon />} color="#f59e0b" sub={`of ${total} total`} />
                        </Grid>
                    </Grid>

                    {statsLoading && <LinearProgress sx={{ mb: 3, borderRadius: '4px' }} />}

                    {stats && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Card sx={{ borderRadius: '20px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, '&:hover': { transform: 'none' } }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: alpha('#6366f1', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TrendingUpIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Daily Signups — Last 30 Days</Typography>
                                        </Stack>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <LineChart data={stats.dailySignups || []}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickFormatter={d => d.slice(5)} interval="preserveStartEnd" />
                                                <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} allowDecimals={false} />
                                                <ChartTooltip contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 8 }} labelStyle={{ fontWeight: 700 }} />
                                                <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={false} name="Signups" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card sx={{ borderRadius: '20px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, height: '100%', '&:hover': { transform: 'none' } }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: alpha('#a855f7', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ChartIcon sx={{ color: '#a855f7', fontSize: 18 }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>By Segment</Typography>
                                        </Stack>
                                        {pieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={180}>
                                                <PieChart>
                                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                                                        {pieData.map((_, i) => <Cell key={i} fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]} />)}
                                                    </Pie>
                                                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                                    <ChartTooltip contentStyle={{ background: theme.palette.background.paper, borderRadius: 8 }} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'text.secondary' }}>
                                                <Typography variant="body2">No segment data yet</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            )}

            {/* ── Tab 1: Subscribers ──────────────────────────────────────────── */}
            {tab === 1 && (
                <Card sx={{ borderRadius: '20px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, '&:hover': { transform: 'none' } }}>
                    <CardContent sx={{ p: 3 }}>
                        {/* Filters row */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems={{ sm: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, flexGrow: 1 }}>Subscribers</Typography>
                            <TextField
                                size="small" placeholder="Search email…" value={search}
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Segment</InputLabel>
                                <Select value={filterSeg} label="Segment" onChange={e => { setFilterSeg(e.target.value); setPage(1); }} sx={{ borderRadius: '12px' }}>
                                    <MenuItem value="">All</MenuItem>
                                    {segments.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={filterStatus} label="Status" onChange={e => { setFilterStatus(e.target.value); setPage(1); }} sx={{ borderRadius: '12px' }}>
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="pending_verification">Pending Verification</MenuItem>
                                    <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
                                    <MenuItem value="bounced">Bounced</MenuItem>
                                    <MenuItem value="complained">Complained</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* Bulk action bar */}
                        {selected.size > 0 && (
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{
                                mb: 2, p: 2, borderRadius: '14px',
                                bgcolor: alpha('#6366f1', isDark ? 0.1 : 0.06),
                                border: `1px solid ${alpha('#6366f1', 0.2)}`,
                            }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, flexGrow: 1, color: '#6366f1' }}>
                                    {selected.size} subscriber{selected.size !== 1 ? 's' : ''} selected
                                </Typography>
                                <Button size="small" variant="outlined" startIcon={bulkLoading ? <CircularProgress size={14} /> : <ResendIcon />}
                                    onClick={handleBulkResend} disabled={bulkLoading}
                                    sx={{ borderRadius: '100px', textTransform: 'none', fontWeight: 700, borderColor: alpha('#6366f1', 0.35), color: '#6366f1', fontSize: '0.8rem', '&:hover': { borderColor: '#6366f1', bgcolor: alpha('#6366f1', 0.06) } }}>
                                    Resend Welcome
                                </Button>
                                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                                    onClick={() => setConfirmBulkDelete(true)} disabled={bulkLoading}
                                    sx={{ borderRadius: '100px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}>
                                    Delete
                                </Button>
                                <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: 'none', color: 'text.disabled', fontSize: '0.8rem' }}>✕ Clear</Button>
                            </Stack>
                        )}

                        {subLoading ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={filteredSubs.length > 0 && selected.size === filteredSubs.length}
                                                    indeterminate={selected.size > 0 && selected.size < filteredSubs.length}
                                                    onChange={toggleSelectAll}
                                                />
                                            </TableCell>
                                            {['Email', 'Segment', 'Role', 'Platforms', 'Priority', 'Status', 'Joined'].map(h => (
                                                <TableCell key={h} sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', borderColor: alpha(theme.palette.divider, 0.1) }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSubs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary', borderColor: 'transparent' }}>
                                                    No subscribers found
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredSubs.map(sub => (
                                            <TableRow
                                                key={sub.id}
                                                hover
                                                selected={selected.has(sub.id)}
                                                onClick={() => setDrawerSub(sub)}
                                                sx={{ cursor: 'pointer', '& td': { borderColor: alpha(theme.palette.divider, 0.06) } }}
                                            >
                                                <TableCell padding="checkbox" onClick={e => { e.stopPropagation(); toggleSelect(sub.id); }}>
                                                    <Checkbox size="small" checked={selected.has(sub.id)} />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                                        <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 800, bgcolor: alpha('#6366f1', 0.15), color: '#6366f1' }}>
                                                            {sub.email?.[0]?.toUpperCase()}
                                                        </Avatar>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{sub.email}</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    {sub.segment && sub.segment !== 'General' && (
                                                        <Chip label={sub.segment} size="small" sx={{ borderRadius: '6px', fontWeight: 700, fontSize: '0.7rem', bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }} />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', textTransform: 'capitalize' }}>{sub.onboarding?.role || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{sub.onboarding?.platforms?.join(', ') || '—'}</TableCell>
                                                <TableCell>
                                                    <Chip label={sub.priority ?? 0} size="small" sx={{ borderRadius: '6px', fontWeight: 800, fontSize: '0.7rem', bgcolor: sub.priority > 10 ? alpha('#10d98a', 0.12) : alpha(theme.palette.divider, 0.1), color: sub.priority > 10 ? '#10d98a' : 'text.secondary' }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={sub.status?.replace(/_/g, ' ')}
                                                        size="small"
                                                        sx={{
                                                            borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem',
                                                            bgcolor: alpha(STATUS_HEX[sub.status] || '#94a3b8', 0.12),
                                                            color: STATUS_HEX[sub.status] || '#94a3b8',
                                                            border: `1px solid ${alpha(STATUS_HEX[sub.status] || '#94a3b8', 0.25)}`,
                                                            textTransform: 'capitalize',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                                    {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {total > perPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Pagination count={Math.ceil(total / perPage)} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* ── Tab 2: Campaigns ────────────────────────────────────────────── */}
            {tab === 2 && (
                <Card sx={{ borderRadius: '20px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, '&:hover': { transform: 'none' } }}>
                    <CardContent sx={{ p: 3 }}>
                        <CampaignsTab refreshKey={campaignRefreshKey} />
                    </CardContent>
                </Card>
            )}

            {/* Subscriber detail drawer */}
            <SubscriberDrawer
                subscriber={drawerSub}
                open={Boolean(drawerSub)}
                onClose={() => setDrawerSub(null)}
                onDeleted={handleSubscriberDeleted}
                onResent={() => {}}
            />

            {/* Broadcast dialog */}
            <BroadcastDialog
                open={broadcastOpen}
                onClose={() => setBroadcastOpen(false)}
                segments={segments}
                onSent={() => { loadStats(); setCampaignRefreshKey(k => k + 1); }}
            />

            {/* Bulk delete confirmation dialog */}
            <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}
                PaperProps={{ sx: { borderRadius: '20px', bgcolor: isDark ? '#0f172a' : 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.12)}` } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Delete {selected.size} subscriber{selected.size !== 1 ? 's' : ''}?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        This will permanently delete the selected subscriber{selected.size !== 1 ? 's' : ''} and all their data. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={() => setConfirmBulkDelete(false)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
                    <Button
                        variant="contained" color="error"
                        onClick={() => { setConfirmBulkDelete(false); handleBulkDelete(); }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                    >
                        Yes, delete all
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminNewsletter;
